import { STORAGE_KEYS } from "@/constants/storage";

type LogLevel = "log" | "info" | "warn" | "error" | "debug";

export type AppLogSettings = {
  enabled: boolean;
  maxEntries: number;
  maxArgLength: number;
  sessionId: string;
  // Local day key in format YYYY-MM-DD; used to run retention once per day
  dayKey: string;
};

export type StoredAppLogEntry = {
  ts: number;
  time: string; // local time string for easier reading
  level: LogLevel;
  args: string[];
  href: string;
  sessionId: string;
  origin?: string; // best-effort "file:line:col" from stacktrace
};

export type CxLogsApi = {
  get: () => StoredAppLogEntry[];
  clear: () => void;
  download: (filename?: string) => void;
  enable: () => void;
  disable: () => void;
  flush: () => void;
  sessionId: () => string;
};

declare global {
  interface Window {
    __cxLogs?: CxLogsApi;
  }
}

const DEFAULT_SETTINGS: AppLogSettings = {
  enabled: true,
  maxEntries: 2000,
  maxArgLength: 4000,
  sessionId: "",
  dayKey: "",
};

const SETTINGS_KEY = STORAGE_KEYS.APP_LOGS_SETTINGS;
const LOGS_KEY = STORAGE_KEYS.APP_LOGS;

const GLOBAL_STATE_KEY = "__CX_APP_LOG_CAPTURE__" as const;

type GlobalState = {
  initialized: boolean;
  settings: AppLogSettings;
  originals: Partial<Record<LogLevel, (...args: unknown[]) => void>>;
  buffer: StoredAppLogEntry[];
  flushTimer: number | null;
  uninstallErrorHandlers: (() => void) | null;
};

function getGlobalState(): GlobalState {
  const w = window as unknown as Record<string, unknown>;
  const existing = w[GLOBAL_STATE_KEY] as GlobalState | undefined;
  if (existing) return existing;

  const created: GlobalState = {
    initialized: false,
    settings: { ...DEFAULT_SETTINGS, sessionId: createSessionId() },
    originals: {},
    buffer: [],
    flushTimer: null,
    uninstallErrorHandlers: null,
  };
  w[GLOBAL_STATE_KEY] = created;
  return created;
}

function createSessionId() {
  // Correlation-only identifier. Use crypto-secure RNG to satisfy security tooling
  // (even though this is not used for authentication/authorization).
  const g = globalThis as unknown as { crypto?: Crypto };
  if (g.crypto?.randomUUID) {
    return `${Date.now().toString(36)}-${g.crypto.randomUUID()}`;
  }
  if (g.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    g.crypto.getRandomValues(bytes);
    // hex string
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${Date.now().toString(36)}-${hex}`;
  }
  // Extremely old browsers only; still unique-ish without Math.random()
  return `${Date.now().toString(36)}-${Date.now().toString(16)}`;
}

function formatLocalTime(ts: number): string {
  const d = new Date(ts);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}.${ms}`;
}

function getTodayKey(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getStartOfToday(now = new Date()) {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function getRetentionCutoffTs(retentionDays: number, now = new Date()) {
  const startOfToday = getStartOfToday(now);
  const cutoff = new Date(startOfToday);
  // retentionDays=2 => keep today + yesterday => cutoff = startOfYesterday
  cutoff.setDate(cutoff.getDate() - Math.max(0, retentionDays - 1));
  return cutoff.getTime();
}

function purgeOldLogs(retentionDays: number, maxEntries: number) {
  const cutoffTs = getRetentionCutoffTs(retentionDays);
  const existing = readLogsUnsafe();
  if (existing.length === 0) return;

  const filtered = existing.filter((e) => typeof e?.ts === "number" && e.ts >= cutoffTs);
  if (filtered.length === existing.length) return;

  writeLogsWithTrim(filtered, maxEntries);
}

function ensureRetention(state: GlobalState) {
  const todayKey = getTodayKey();
  if (state.settings.dayKey === todayKey) return;

  // Run retention on first log/init of the day; keep today + yesterday
  purgeOldLogs(2, state.settings.maxEntries);
  state.settings = {
    ...state.settings,
    dayKey: todayKey,
  };
  writeSettings(state.settings);
}

function parseBoolean(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (typeof v !== "string") return null;
  const s = v.trim().toLowerCase();
  if (s === "1" || s === "true" || s === "yes" || s === "on") return true;
  if (s === "0" || s === "false" || s === "no" || s === "off") return false;
  return null;
}

function safeStringify(value: unknown, maxLen: number): string {
  try {
    if (value instanceof Error) {
      const base = `${value.name}: ${value.message}`;
      const stack = value.stack ? `\n${value.stack}` : "";
      return truncate(`${base}${stack}`, maxLen);
    }
    if (typeof value === "string") return truncate(value, maxLen);
    if (value === null || value === undefined) return String(value);
    if (typeof value === "bigint") return truncate(`${value.toString()}n`, maxLen);

    // JSON stringify with circular protection
    const seen = new WeakSet<object>();
    const json = JSON.stringify(
      value,
      (_k, v) => {
        if (typeof v === "bigint") return `${v.toString()}n`;
        if (v instanceof Error) return { name: v.name, message: v.message, stack: v.stack };
        if (typeof v === "object" && v !== null) {
          const obj = v as object;
          if (seen.has(obj)) return "[Circular]";
          seen.add(obj);
        }
        return v;
      },
      2
    );
    return truncate(json, maxLen);
  } catch {
    try {
      return truncate(String(value), maxLen);
    } catch {
      return "[Unserializable]";
    }
  }
}

function truncate(s: string, maxLen: number) {
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…[truncated]`;
}

function stripQueryHash(url: string) {
  return url.split("#")[0].split("?")[0];
}

function getFileNameFromUrl(url: string) {
  const clean = stripQueryHash(url);
  const lastSlash = Math.max(clean.lastIndexOf("/"), clean.lastIndexOf("\\"));
  return lastSlash >= 0 ? clean.slice(lastSlash + 1) : clean;
}

type FrameLoc = { cleanUrl: string; line: number; col?: number };

function v8RestAfterAt(line: string): string | null {
  // `line` is typically already trimmed. V8 stack frames look like:
  // "at foo (http://...:10:2)" or "at http://...:10:2"
  if (!line.startsWith("at")) return null;
  if (line.length <= 2) return null;
  const c = line[2];
  // Require at least one whitespace after "at" (matches previous /^\s*at\s+(.*)$/ behavior after trim()).
  if (c !== " " && c !== "\t") return null;
  let i = 2;
  while (i < line.length) {
    const ch = line[i];
    if (ch !== " " && ch !== "\t") break;
    i++;
  }
  if (i >= line.length) return null;
  return line.slice(i);
}

function v8LocPartFromRest(rest: string): string {
  const trimmed = rest.trim();
  // If the location is in trailing parentheses, extract the inside.
  if (trimmed.endsWith(")")) {
    const closeIdx = trimmed.length - 1;
    const openIdx = trimmed.lastIndexOf("(", closeIdx);
    if (openIdx >= 0 && openIdx < closeIdx) {
      const inside = trimmed.slice(openIdx + 1, closeIdx).trim();
      if (inside) return inside;
    }
  }
  return trimmed;
}

function ffRestAfterAt(line: string): string | null {
  // Avoid regex backtracking on untrusted stack strings.
  // Firefox stack frames look like: "fn@http://.../file.ts:10:2"
  const idx = line.indexOf("@");
  if (idx < 0) return null;
  return line.slice(idx + 1);
}

function extractFirstNFrameLocs(stack: string, n: number): FrameLoc[] {
  const out: FrameLoc[] = [];
  const lines = stack.split("\n");
  for (const rawLine of lines) {
    if (out.length >= n) break;
    const line = rawLine.trim();
    if (!line) continue;

    let locPart = "";
    const v8Rest = v8RestAfterAt(line);
    if (v8Rest) {
      locPart = v8LocPartFromRest(v8Rest);
    } else {
      const ffRest = ffRestAfterAt(line);
      locPart = ffRest ? ffRest.trim() : "";
    }
    if (!locPart) continue;

    // Prefer :line:col, but accept :line if column is missing
    const m3 = locPart.match(/^(.*):(\d+):(\d+)\s*$/);
    if (m3) {
      const url = stripQueryHash(m3[1].trim());
      const lineNum = Number(m3[2]);
      const colNum = Number(m3[3]);
      if (Number.isFinite(lineNum) && Number.isFinite(colNum)) out.push({ cleanUrl: url, line: lineNum, col: colNum });
      continue;
    }
    const m2 = locPart.match(/^(.*):(\d+)\s*$/);
    if (m2) {
      const url = stripQueryHash(m2[1].trim());
      const lineNum = Number(m2[2]);
      if (Number.isFinite(lineNum)) out.push({ cleanUrl: url, line: lineNum });
    }
  }
  return out;
}

function getLogOriginFromStack(stack: string | undefined | null): string | undefined {
  try {
    const text = typeof stack === "string" ? stack : "";
    if (!text) return undefined;

    const lines = text.split("\n");
    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) continue;

      // V8:
      // "at foo (http://.../src/x.ts:10:2)"
      // "at http://.../src/x.ts:10:2"
      // Firefox:
      // "foo@http://.../src/x.ts:10:2"

      let locPart = "";
      const v8Rest = v8RestAfterAt(line);
      if (v8Rest) {
        locPart = v8LocPartFromRest(v8Rest);
      } else {
        const ffRest = ffRestAfterAt(line);
        locPart = ffRest ? ffRest.trim() : "";
      }
      if (!locPart) continue;

      const locMatch = locPart.match(/^(.*):(\d+):(\d+)\s*$/);
      if (!locMatch) continue;

      const url = locMatch[1].trim();
      const lineNum = Number(locMatch[2]);
      const colNum = Number(locMatch[3]);
      if (!Number.isFinite(lineNum) || !Number.isFinite(colNum)) continue;

      const cleanUrl = stripQueryHash(url);
      const fileName = getFileNameFromUrl(cleanUrl);

      // Skip frames coming from this logger itself (wrapper)
      if (fileName.includes("appLogCapture")) continue;
      if (cleanUrl.includes("/src/utils/appLogCapture")) continue;
      if (cleanUrl.includes("\\src\\utils\\appLogCapture")) continue;

      // Skip obvious non-user frames
      if (cleanUrl.includes("/node_modules/") || cleanUrl.includes("\\node_modules\\")) continue;
      if (cleanUrl.includes("@vite") || cleanUrl.includes("/@fs/")) continue;

      return truncate(`${cleanUrl}:${lineNum}:${colNum}`, 300);
    }
  } catch {
    // ignore
  }
  return undefined;
}

function getLogOrigin(): string | undefined {
  try {
    const err = new Error();
    return getLogOriginFromStack(err.stack);
  } catch {
    return undefined;
  }
}

type AnyFn = (...args: unknown[]) => unknown;

function captureStackAfter(skipFn: AnyFn): string | null {
  try {
    // V8/Chromium: omit frames up to and including skipFn
    const cap = Error as unknown as { captureStackTrace?: (target: { stack?: string }, constructorOpt?: AnyFn) => void };
    if (typeof cap.captureStackTrace === "function") {
      const target: { stack?: string } = {};
      cap.captureStackTrace(target, skipFn);
      return typeof target.stack === "string" ? target.stack : null;
    }
    // Fallback (Firefox/Safari): best effort, then drop our own first frames
    const e = new Error();
    const stack = typeof e.stack === "string" ? e.stack : null;
    if (!stack) return null;

    // When captureStackTrace is unavailable, the first frames are usually:
    // captureStackAfter -> patched(console wrapper) -> real caller
    const toSkip = extractFirstNFrameLocs(stack, 2);
    if (toSkip.length === 0) return stack;

    const filtered = stack
      .split("\n")
      .filter((rawLine, idx) => {
        if (idx === 0) return true; // keep "Error" line if present
        const line = rawLine.trim();
        if (!line) return true;
        return !toSkip.some((f) => {
          if (!line.includes(f.cleanUrl)) return false;
          if (f.col != null) return line.includes(`:${f.line}:${f.col}`);
          return line.includes(`:${f.line}`);
        });
      })
      .join("\n");

    return filtered || stack;
  } catch {
    return null;
  }
}

function readSettings(): AppLogSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS, sessionId: createSessionId() };
    const parsed = JSON.parse(raw) as Partial<AppLogSettings>;

    const enabled = parsed.enabled ?? DEFAULT_SETTINGS.enabled;
    const maxEntries =
      typeof parsed.maxEntries === "number" && Number.isFinite(parsed.maxEntries) ? parsed.maxEntries : DEFAULT_SETTINGS.maxEntries;
    const maxArgLength =
      typeof parsed.maxArgLength === "number" && Number.isFinite(parsed.maxArgLength) ? parsed.maxArgLength : DEFAULT_SETTINGS.maxArgLength;
    const sessionId = typeof parsed.sessionId === "string" && parsed.sessionId.length > 0 ? parsed.sessionId : createSessionId();
    const dayKey = typeof parsed.dayKey === "string" && parsed.dayKey.length > 0 ? parsed.dayKey : getTodayKey();

    return { enabled, maxEntries, maxArgLength, sessionId, dayKey };
  } catch {
    return { ...DEFAULT_SETTINGS, sessionId: createSessionId() };
  }
}

function writeSettings(settings: AppLogSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignore storage errors
  }
}

function readLogsUnsafe(): StoredAppLogEntry[] {
  try {
    const raw = localStorage.getItem(LOGS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredAppLogEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLogsWithTrim(logs: StoredAppLogEntry[], maxEntries: number) {
  const trimmed = logs.length > maxEntries ? logs.slice(logs.length - maxEntries) : logs;

  // Best-effort write with quota handling: progressively drop oldest
  for (let dropFactor = 0; dropFactor < 3; dropFactor++) {
    try {
      localStorage.setItem(LOGS_KEY, JSON.stringify(trimmed));
      return;
    } catch {
      // drop more aggressively and retry
      const keep = Math.max(50, Math.floor(trimmed.length / 2));
      trimmed.splice(0, Math.max(0, trimmed.length - keep));
    }
  }
}

function scheduleFlush(state: GlobalState) {
  if (state.flushTimer != null) return;
  state.flushTimer = window.setTimeout(() => {
    state.flushTimer = null;
    flushNow();
  }, 300);
}

function flushNow() {
  const state = getGlobalState();
  if (state.buffer.length === 0) return;

  const { maxEntries } = state.settings;
  const existing = readLogsUnsafe();
  const merged = existing.concat(state.buffer);
  state.buffer = [];
  writeLogsWithTrim(merged, maxEntries);
}

function record(level: LogLevel, args: unknown[], originStack?: string | null) {
  const state = getGlobalState();
  ensureRetention(state);
  if (!state.settings.enabled) return;

  const ts = Date.now();
  const entry: StoredAppLogEntry = {
    ts,
    time: formatLocalTime(ts),
    level,
    args: args.map((a) => safeStringify(a, state.settings.maxArgLength)),
    href: window.location.href,
    sessionId: state.settings.sessionId,
    origin: getLogOriginFromStack(originStack) ?? getLogOrigin(),
  };

  state.buffer.push(entry);
  if (state.buffer.length >= 20) {
    flushNow();
  } else {
    scheduleFlush(state);
  }
}

function installErrorHandlers(state: GlobalState) {
  const onError = (event: ErrorEvent) => {
    record("error", ["window.error", event.message, event.error], event.error?.stack ?? null);
  };
  const onUnhandled = (event: PromiseRejectionEvent) => {
    const reason = event.reason as unknown;
    const reasonStack = reason instanceof Error ? reason.stack : null;
    record("error", ["unhandledrejection", event.reason], reasonStack);
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandled);

  state.uninstallErrorHandlers = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandled);
  };
}

function installConsolePatches(state: GlobalState) {
  const methods: LogLevel[] = ["log", "info", "warn", "error", "debug"];
  for (const level of methods) {
    const original = console[level].bind(console);
    state.originals[level] = original;

    const patched = (...args: unknown[]) => {
      // Capture real callsite (skip this wrapper) so origin points to the caller, not the logger.
      const stack = captureStackAfter(patched);
      record(level, args, stack);
      // Always mirror to console (default browser behavior)
      original(...args);
    };
    console[level] = patched;
  }
}

function maybeApplyRuntimeOverrides(state: GlobalState) {
  // lightweight overrides for support:
  // - localStorage[APP_LOGS_SETTINGS].enabled = false
  try {
    // Support simple toggles without JSON editing:
    const enabledOverride = parseBoolean(localStorage.getItem("cx_logs_enabled"));
    if (enabledOverride != null) {
      state.settings = {
        ...state.settings,
        ...(enabledOverride != null ? { enabled: enabledOverride } : null),
      };
      writeSettings(state.settings);
    }
  } catch {
    // ignore
  }
}

export function initAppLogCapture() {
  // Log capture is only enabled in production mode
  if (import.meta.env.MODE !== "production") {
    console.log("%c✗ App log capture disabled in non-production mode", "color: #92400e; font-weight: 800;");
    return;
  }

  console.log("%c✓ App log capture enabled in production mode", "color: #065f46; font-weight: 800;");

  const state = getGlobalState();
  if (state.initialized) return;

  state.settings = readSettings();
  // Ensure retention immediately at startup (today + yesterday)
  ensureRetention(state);
  maybeApplyRuntimeOverrides(state);

  installConsolePatches(state);
  installErrorHandlers(state);

  // Expose minimal helpers for support/QA from DevTools
  window.__cxLogs = {
    get: getStoredAppLogs,
    clear: clearStoredAppLogs,
    download: downloadStoredAppLogs,
    enable: () => setAppLogCaptureEnabled(true),
    disable: () => setAppLogCaptureEnabled(false),
    flush: flushNow,
    sessionId: () => getGlobalState().settings.sessionId,
  };

  state.initialized = true;
  record("info", ["App log capture initialized", { prod: import.meta.env.PROD }]);
}

export function setAppLogCaptureEnabled(enabled: boolean) {
  const state = getGlobalState();
  state.settings = { ...state.settings, enabled };
  writeSettings(state.settings);
}

export function getStoredAppLogs(): StoredAppLogEntry[] {
  // include unflushed buffer to avoid missing last moments
  const state = getGlobalState();
  const existing = readLogsUnsafe();
  return existing.concat(state.buffer);
}

export function clearStoredAppLogs() {
  const state = getGlobalState();
  state.buffer = [];
  try {
    localStorage.removeItem(LOGS_KEY);
  } catch {
    // ignore
  }
}

export function downloadStoredAppLogs(filename = "app-logs.json") {
  try {
    const payload = JSON.stringify(getStoredAppLogs(), null, 2);
    const blob = new Blob([payload], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    // If download fails, still try to record why
    record("error", ["Failed to download logs", e]);
  }
}

